import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { ASSET_NAME } from 'src/app/app.consts';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import { ApplicationService } from 'src/app/services/application-service';
import { handleApiError } from 'src/app/utils/error';
import { FlowService } from 'src/app/services/flow.service';
import { take } from 'rxjs/operators';
import { TrackingService } from 'src/app/services/tracking.service';

@Component({
  selector: 'app-real-estate',
  templateUrl: './real-estate.component.html',
  styleUrls: ['./real-estate.component.scss'],
})
export class RealEstateComponent implements OnInit, OnDestroy {
  applicationId!: number;
  realEstateForm!: FormGroup;
  error!: string;
  assetName!: typeof ASSET_NAME;
  subs: Array<Subscription> = [];
  requestPending = false;
  @Input() isSummary!: boolean;

  options = [
    { name: this.translateService.instant('realEstate.realEstate'), value: 1 },
    { name: this.translateService.instant('realEstate.notExist'), value: 2 },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private applicationService: ApplicationService,
    private applicationsApiService: ApplicationsApiService,
    public flowService: FlowService,
    protected trackingService: TrackingService
  ) {
  }

  ngOnInit(): void {
    this.applicationId = this.applicationService.applicationId;

    const initialRealEstate = this.applicationService.realEstate || null;
    const initialPropertyDescription =
      this.applicationService.propertyDescription || '';
    const initialRealEstateFiles =
      this.applicationService.realEstateFiles || [];

    this.realEstateForm = this.formBuilder.group({
      realEstate: this.formBuilder.control(initialRealEstate),
      propertyDescription: this.formBuilder.control(initialPropertyDescription, { updateOn: 'blur' }),
      realEstateFiles: this.formBuilder.array(initialRealEstateFiles),
    });
    this.assetName = ASSET_NAME;

    this.subs.push(this.realEstate.valueChanges.subscribe((v) => {
        this.requestPending = true;
        this.applicationService.realEstate = v;
        const realEstateValue =
          this.realEstate.value === 2
            ? 'no real estate'
            : this.propertyDescription.value;

        this.applicationsApiService
          .addSecurity({
            realEstate: realEstateValue,
            applicationId: this.applicationId,
          })
          .pipe(take(1))
          .subscribe(
            () => {
              this.error = '';
              if (realEstateValue === 'no real estate') {
                this.trackFields('noRealEstate');
              } else {
                this.trackFields('realEstate');
              }
            },
            (error: any) => handleApiError(error, this.translateService),
            () => this.requestPending = false
          );
      })
    );
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  propertyDescriptionUpdate(): void {
    this.requestPending = true;
    const description = this.propertyDescription.value;
    this.applicationService.propertyDescription = description;
    this.applicationsApiService
      .addSecurity({
        realEstate: description,
        applicationId: this.applicationId,
      })
      .pipe(take(1))
      .subscribe(
        () => {
          this.error = '';
          this.trackFields('realEstateDesc');
        },
        (error: any) => handleApiError(error, this.translateService),
        () => {
          this.requestPending = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }

  handleBackButtonClick(): void {
    this.subs.push(timer(1000, 1000).subscribe(() => {
      if (!this.requestPending) {
        this.flowService.previousScreen();
      }
    }));
  }

  handleNextButtonClick(): void {
    this.subs.push(timer(1000, 1000).subscribe(() => {
      if (!this.requestPending) {
        this.flowService.nextScreen();
      }
    }));
  }

  get form(): FormGroup {
    return this.realEstateForm;
  }

  get realEstate(): FormControl {
    return this.form.get('realEstate') as FormControl;
  }

  get propertyDescription(): FormControl {
    return this.form.get('propertyDescription') as FormControl;
  }

  get realEstateFiles(): FormArray {
    return this.form.get('realEstateFiles') as FormArray;
  }
}
